import type { Accidentals, Notation } from '@/core/theory/display';
import { SyncCalibrator } from '@/features/settings';
import { COMPLEXITY_OPTIONS } from '@/features/transpose';
import { SHORTCUTS } from '@/lib/shortcuts';
import type { AppSettings, Instrument } from '@/storage/types';
import { useSettingsStore } from '@/state/settingsStore';
import { Segmented } from '@/ui/Segmented';
import styles from './SettingsPage.module.css';

const CHORD_MODELS: { value: AppSettings['chordModel']; label: string }[] = [
  { value: 'btc', label: 'AI model (BTC)' },
  { value: 'classic', label: 'Classic' },
];

const SECTIONS = [
  { id: 'general', label: 'General' },
  { id: 'sync', label: 'Playback sync' },
  { id: 'storage', label: 'Your data' },
  { id: 'shortcuts', label: 'Keyboard shortcuts' },
  { id: 'privacy', label: 'Privacy & about' },
];

const THEMES = [
  { value: 'light' as const, label: 'Light' },
  { value: 'dark' as const, label: 'Dark' },
  { value: 'system' as const, label: 'Match system' },
];

const INSTRUMENTS: { value: Instrument; label: string }[] = [
  { value: 'guitar', label: 'Guitar' },
  { value: 'ukulele', label: 'Ukulele' },
  { value: 'mandolin', label: 'Mandolin' },
  { value: 'bass', label: 'Bass guitar' },
  { value: 'piano', label: 'Piano' },
];

const WARNINGS = [
  { value: 0 as const, label: 'Off' },
  { value: 1 as const, label: '1 beat' },
  { value: 2 as const, label: '2 beats' },
  { value: 4 as const, label: '4 beats' },
];

const NOTATIONS: { value: Notation; label: string }[] = [
  { value: 'letters', label: 'Letters (C D E)' },
  { value: 'german', label: 'German (H for B)' },
  { value: 'solfege', label: 'Solfège (Do Re Mi)' },
  { value: 'roman', label: 'Roman numerals' },
  { value: 'nashville', label: 'Nashville numbers' },
];

const ACCIDENTALS = [
  { value: 'auto' as const, label: 'By key', ariaLabel: 'Spell by key' },
  { value: 'sharps' as const, label: '♯', ariaLabel: 'Always sharps' },
  { value: 'flats' as const, label: '♭', ariaLabel: 'Always flats' },
];

/** App settings (F10.3; design: Settings.dc.html). Changes save immediately. */
export function SettingsPage() {
  const settings = useSettingsStore();
  const update = (patch: Partial<AppSettings>) => {
    settings.update(patch);
  };

  return (
    <main className={styles.page}>
      <h1 className={styles.title}>Settings</h1>
      <div className={styles.layout}>
        <nav aria-label="Settings sections" className={styles.nav}>
          {SECTIONS.map((s) => (
            <a key={s.id} href={`#${s.id}`} className={styles.navLink}>
              {s.label}
            </a>
          ))}
        </nav>

        <div className={styles.sections}>
          <section id="general" aria-labelledby="general-h" className={styles.card}>
            <h2 id="general-h" className={styles.cardTitle}>
              General
            </h2>
            <div className={styles.field}>
              <span className={styles.label}>Theme</span>
              <Segmented
                label="Theme"
                options={THEMES}
                value={settings.theme}
                onChange={(theme) => {
                  update({ theme });
                }}
              />
            </div>
            <label className={styles.field}>
              <span className={styles.label}>Default instrument for new songs</span>
              <select
                value={settings.defaultInstrument}
                onChange={(e) => {
                  update({ defaultInstrument: e.target.value as Instrument });
                }}
              >
                {INSTRUMENTS.map((i) => (
                  <option key={i.value} value={i.value}>
                    {i.label}
                  </option>
                ))}
              </select>
            </label>
            <label className={styles.field}>
              <span className={styles.label}>Chord notation</span>
              <select
                value={settings.notation}
                onChange={(e) => {
                  update({ notation: e.target.value as Notation });
                }}
              >
                {NOTATIONS.map((n) => (
                  <option key={n.value} value={n.value}>
                    {n.label}
                  </option>
                ))}
              </select>
            </label>
            <div className={styles.field}>
              <span className={styles.label}>Sharps / flats</span>
              <Segmented
                label="Sharps or flats"
                options={ACCIDENTALS}
                value={settings.accidentals}
                onChange={(accidentals: Accidentals) => {
                  update({ accidentals });
                }}
              />
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Chord detail</span>
              <Segmented
                label="Chord detail"
                options={COMPLEXITY_OPTIONS}
                value={settings.complexity}
                onChange={(complexity) => {
                  update({ complexity });
                }}
              />
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Chord recognition</span>
              <Segmented
                label="Chord recognition"
                options={CHORD_MODELS}
                value={settings.chordModel}
                onChange={(chordModel) => {
                  update({ chordModel });
                }}
              />
              <span className={styles.text}>
                The AI model (BTC) is usually more accurate. Files are analysed with it on this
                device (a one-time download of about 27 MB); YouTube songs on the server.
              </span>
            </div>
            <div className={styles.field}>
              <span className={styles.label}>Warn before a chord change</span>
              <Segmented
                label="Change warning"
                options={WARNINGS}
                value={settings.changeWarningBeats}
                onChange={(changeWarningBeats) => {
                  update({ changeWarningBeats });
                }}
              />
            </div>
            <label className={styles.check}>
              <input
                type="checkbox"
                checked={settings.easyMode}
                onChange={(e) => {
                  update({ easyMode: e.target.checked });
                }}
              />
              Easy mode: plain chords and the easiest shapes
            </label>
            <label className={styles.check}>
              <input
                type="checkbox"
                checked={settings.reduceMotion}
                onChange={(e) => {
                  update({ reduceMotion: e.target.checked });
                }}
              />
              Reduce motion (no gliding, pulsing or smooth scrolling)
            </label>
            <label className={styles.check}>
              <input
                type="checkbox"
                checked={settings.leftHanded}
                onChange={(e) => {
                  update({ leftHanded: e.target.checked });
                }}
              />
              Left-handed chord diagrams
            </label>
          </section>

          <section id="sync" aria-labelledby="sync-h" className={styles.card}>
            <h2 id="sync-h" className={styles.cardTitle}>
              Playback sync
            </h2>
            <SyncCalibrator
              valueMs={settings.visualOffsetMs}
              onChange={(visualOffsetMs) => {
                update({ visualOffsetMs });
              }}
            />
          </section>

          <section id="storage" aria-labelledby="storage-h" className={styles.card}>
            <h2 id="storage-h" className={styles.cardTitle}>
              Your data
            </h2>
            <p className={styles.text}>
              Songs, their chords and their audio are kept only while this tab is open. Nothing
              about your songs is saved: reloading or closing the tab clears them. Only the
              preferences on this page are remembered, in this browser.
            </p>
          </section>

          <section id="shortcuts" aria-labelledby="shortcuts-h" className={styles.card}>
            <h2 id="shortcuts-h" className={styles.cardTitle}>
              Keyboard shortcuts
            </h2>
            <dl className={styles.shortcuts}>
              {SHORTCUTS.map(([what, key]) => (
                <div key={what}>
                  <dt>{what}</dt>
                  <dd>
                    <kbd>{key}</kbd>
                  </dd>
                </div>
              ))}
            </dl>
            <p className={styles.text}>
              Page-turner pedals usually send PageUp / PageDown, which move by a bar.
            </p>
          </section>

          <section
            id="privacy"
            aria-labelledby="privacy-h"
            className={`${styles.card} ${styles.privacy}`}
          >
            <h2 id="privacy-h" className={styles.cardTitle}>
              Privacy & about
            </h2>
            <ul className={styles.privacyList}>
              <li>
                Your audio is decoded and analyzed on this device. It is never uploaded or stored.
              </li>
              <li>
                Nothing about your songs is saved; only your preferences. There are no accounts.
              </li>
              <li>The app makes no requests to other servers and has no analytics.</li>
            </ul>
            <p className={styles.about}>
              KnowYourChord {__APP_VERSION__} ·{' '}
              <a href="/licenses.txt" target="_blank" rel="noreferrer">
                Third-party licenses
              </a>
            </p>
          </section>
        </div>
      </div>
    </main>
  );
}
